import React, { PropsWithChildren, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useTradeRoutesConditions } from '@pages/Trade/Trade.routes'

import { Button, ButtonProps } from '@ui/components/Button'
import { Link } from '@ui/components/Link'

import {
  DemoTradingAccount,
  TradingAccount,
  isPlatformTypeTickmillTrader,
} from '@model/TradingAccount'

import { usePlatformTradeUrl } from '@hooks/usePlatformTradeUrl'

import { Box } from '@shared/components/Box'

export interface ButtonLinkWrapperProps extends PropsWithChildren {
  disabled: boolean | undefined
  tradingAccount: TradingAccountType
  size?: 'sm' | 'md' | 'lg' | 'xl'
  fullWidth?: boolean
}

interface TradeClickProps {
  tradingAccount: TradingAccountType
}

type TradingAccountType = TradingAccount | DemoTradingAccount

export const useTradeClick = (props: TradeClickProps) => {
  const { tradingAccount } = props

  const navigate = useNavigate()
  const location = useLocation()

  const { canAccessTrade } = useTradeRoutesConditions()
  const { handlePlatformInNewTabOpen } = useTradeTickmillTraderClick({ tradingAccount })

  const [URL, setURL] = useState<string | null>('')
  const [isLoading, setIsLoading] = useState(false)

  const isURLNotFound = URL === null

  const handleTradeClick = async () => {
    const isOnTradePage = location.pathname.includes('/dashboard/trade/markets')

    if (isPlatformTypeTickmillTrader(tradingAccount) && canAccessTrade && !isOnTradePage) {
      navigateToMarkets(tradingAccount)
      return
    }

    const platformTradeUrl = await handlePlatformInNewTabOpen()
    platformTradeUrl && setURL(platformTradeUrl)
  }

  const navigateToMarkets = (tradingAccount: TradingAccountType) => {
    navigate('/dashboard/trade/markets', {
      state: {
        selectedTradingAccount: {
          id: tradingAccount.id,
          showReadyToStartTradingModal: false,
        },
      },
    })
  }

  return { handleTradeClick, URL, isURLNotFound, isLoading, setIsLoading }
}

export const useTradeTickmillTraderClick = (props: TradeClickProps) => {
  const { tradingAccount } = props

  const { getPlatformTradeUrl } = usePlatformTradeUrl()

  const [isLoading, setIsLoading] = useState(false)

  const handlePlatformInNewTabOpen = async () => {
    const platformTradeUrl = await fetchPlatformByTradingAccount(tradingAccount)
    platformTradeUrl && openPlatformInNewTab(platformTradeUrl)
    return platformTradeUrl
  }

  const openPlatformInNewTab = async (platformTradeUrl: string) => {
    const newWindow = window.open('about:blank', '_blank')
    if (platformTradeUrl && newWindow) {
      newWindow.location.href = platformTradeUrl
    }
  }

  const fetchPlatformByTradingAccount = async (tradingAccount: TradingAccountType) => {
    setIsLoading(true)
    try {
      return await getPlatformTradeUrl(tradingAccount)
    } finally {
      setIsLoading(false)
    }
  }

  return { isLoading, handlePlatformInNewTabOpen }
}

export const TradeButtonLinkWrapper: React.FC<ButtonLinkWrapperProps> = (props) => {
  const { disabled, children, size = 'sm', fullWidth } = props

  const { t } = useTranslation()
  const { URL, isLoading, handleTradeClick } = useTradeClick(props)

  const childrenWithProps = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<ButtonProps>, {
        disabled,
      })
    : children

  if (disabled) {
    return childrenWithProps
  }

  if (URL) {
    return (
      <Link href={URL} rel='noreferrer' target='_blank'>
        {children}
      </Link>
    )
  }

  return (
    <Button.Primary
      fullWidth={fullWidth}
      size={size}
      disabled={isLoading}
      loading={isLoading}
      onClick={handleTradeClick}
    >
      {t('Trade')}
    </Button.Primary>
  )
}

export const MobileTradeButtonLinkWrapper: React.FC<
  Omit<ButtonLinkWrapperProps, 'disabled' | 'children'>
> = (props): JSX.Element => {
  const { t } = useTranslation()
  const { size = 'md' } = props

  const { URL, isLoading, handleTradeClick } = useTradeClick(props)

  if (URL) {
    return (
      <Box>
        <Link href={URL} target='_blank' rel='noreferrer'>
          <Button.Primary size={size} fullWidth>
            {t('Trade')}
          </Button.Primary>
        </Link>
      </Box>
    )
  }

  return (
    <Box>
      <Button
        size={size}
        fullWidth
        disabled={isLoading}
        loading={isLoading}
        onClick={handleTradeClick}
      >
        {t('Trade')}
      </Button>
    </Box>
  )
}
